import React, { useState } from "react";
import { usePortfolioQuery } from "../../redux/features/user/userApi";

const numberOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") {
    const abs = Math.abs(value);
    const decimals = abs >= 100 ? 2 : abs >= 1 ? 3 : 6;
    return value.toFixed(decimals);
  }
  return String(value);
};

// Account label mapping
const accountLabels = {
  1: "Main Account",
  2: "Sub Account 1",
  3: "Sub Account 2",
};

const PortfolioU = ({ id, userName }) => {
  const [portfolio, setPortfolio] = useState(1); // default main:1
  const { data, isLoading, isError, error, refetch } = usePortfolioQuery({
    id,
    portfolio,
  });

  const rows = Array.isArray(data?.data) ? data.data : [];

  // 🔽 Export CSV function
  const exportCSV = () => {
    if (!rows.length) return;

    const headers = [
      "Symbol",
      "Quantity",
      "Avg Price",
      "Total Investment",
      "Live Price",
      "Day Gain (%)",
      "Overall Gain (%)",
      "Latest Value",
    ];

    // Info row (user ID / user name + account type)
    const infoRow = [
      `User: ${userName || id || "Unknown User"}`,
      `Account: ${accountLabels[portfolio]}`,
    ].join(",");

    const csvRows = [
      infoRow, // extra row at the top
      headers.join(","), // header row
      ...rows.map((row) =>
        [
          row?._id || "-",
          numberOrDash(row?.Quantity),
          numberOrDash(row?.AvgPrice),
          numberOrDash(row?.Total_Investment),
          numberOrDash(row?.Live_Price),
          numberOrDash(row?.Day_Gain),
          numberOrDash(row?.Overall_Gain),
          numberOrDash(row?.Latest_Value),
        ].join(",")
      ),
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = `portfolio_${userName || id}_${portfolio}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full p-4 bg-gray-50 rounded-lg shadow-sm">
      {/* Header with dropdown + export */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-bold text-gray-900">Portfolio</h2>

        <div className="flex items-center gap-3">
          <select
            value={portfolio}
            onChange={(e) => setPortfolio(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-4 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value={1}>Main Account</option>
            <option value={2}>Sub Account 1</option>
            <option value={3}>Sub Account 2</option>
          </select>

          <button
            onClick={exportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-md shadow hover:bg-green-700 text-sm"
            disabled={!rows.length}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="w-full flex items-center justify-center py-10">
          <span className="text-sm text-gray-600">Loading portfolio...</span>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="w-full flex flex-col items-center justify-center py-10">
          <span className="text-sm text-red-600 mb-3">
            Failed to load portfolio
          </span>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700"
            onClick={() => refetch()}
          >
            Retry
          </button>
          {error?.data?.message && (
            <span className="text-xs text-gray-500 mt-2">
              {error.data.message}
            </span>
          )}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && !rows.length && (
        <div className="w-full flex items-center justify-center py-10">
          <span className="text-sm text-gray-600">No portfolio data</span>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && rows.length > 0 && (
        <div className="w-full overflow-x-auto">
          <table className="min-w-[900px] w-full border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-blue-950 text-white text-sm">
                <th className="p-3 text-left">Symbol</th>
                <th className="p-3 text-right">Quantity</th>
                <th className="p-3 text-right">Avg Price</th>
                <th className="p-3 text-right">Total Investment</th>
                <th className="p-3 text-right">Live Price</th>
                <th className="p-3 text-right">Day Gain (%)</th>
                <th className="p-3 text-right">Overall Gain (%)</th>
                <th className="p-3 text-right">Latest Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const dayGainPos = (row?.Day_Gain ?? 0) >= 0;
                const overallGainPos = (row?.Overall_Gain ?? 0) >= 0;
                return (
                  <tr
                    key={`${row?._id || idx}`}
                    className={`text-sm ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100 transition`}
                  >
                    <td className="p-3 font-medium uppercase text-gray-800">
                      {row?._id || "-"}
                    </td>
                    <td className="p-3 text-right">{numberOrDash(row?.Quantity)}</td>
                    <td className="p-3 text-right">{numberOrDash(row?.AvgPrice)}</td>
                    <td className="p-3 text-right">{numberOrDash(row?.Total_Investment)}</td>
                    <td className="p-3 text-right">{numberOrDash(row?.Live_Price)}</td>
                    <td
                      className={`p-3 text-right font-semibold ${
                        dayGainPos ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {numberOrDash(row?.Day_Gain)}
                    </td>
                    <td
                      className={`p-3 text-right font-semibold ${
                        overallGainPos ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {numberOrDash(row?.Overall_Gain)}
                    </td>
                    <td className="p-3 text-right">{numberOrDash(row?.Latest_Value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortfolioU;
